import { authedFetch } from '../auth';
import { API_BASE_URL } from '../config';

export async function exportRatingsRange(from, to, typeKey, templateId) {
  if (!typeKey || !templateId) {
    throw new Error('Избери тип и темплейт за експорт.');
  }

  const q = new URLSearchParams({
    from,
    to,
    t: typeKey,            // 'online' | 'campaign'
    template_id: templateId
  }).toString();

  const url = `${API_BASE_URL}/ratings/export-range?${q}`;
  const res = await authedFetch(url, {
    headers: {
      "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    },
  });

  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) msg = j.detail;
    } catch {
      try { msg = await res.text(); } catch {}
    }
    throw new Error(msg);
  }

  let filename = `ratings_${typeKey}_${from}_${to}.xlsx`;
  const cd = res.headers.get("Content-Disposition") || res.headers.get("content-disposition") || "";
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  if (m) {
    try { filename = decodeURIComponent(m[1]); } catch { filename = m[1]; }
  }

  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function audioUrl(id) {
  const r = await authedFetch(`${API_BASE_URL}/audio/${encodeURIComponent(id)}`);
  if (!r.ok) throw new Error('Audio URL failed');
  return r.json();
}

export async function exportRatingExcelSilent(id) {
  const res = await authedFetch(`${API_BASE_URL}/ratings/${encodeURIComponent(id)}/excel`, { method: 'GET' });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${t}`);
  }
  await res.blob();
}

export async function getRating(id) {
  const r = await authedFetch(`${API_BASE_URL}/ratings/${id}`);
  if (!r.ok) throw new Error('Get rating failed');
  return r.json();
}

export async function saveRating(id, data) {
  const r = await authedFetch(`${API_BASE_URL}/ratings/${id}`, {
    method:'POST',
    headers:{ 'Content-Type':'application/json' },
    body: JSON.stringify(data)
  });
  if (!r.ok) throw new Error('Save rating failed');
  return r.json();
}

export async function downloadRatingExcel(id) {
  const res = await authedFetch(`${API_BASE_URL}/ratings/${id}/excel`);
  if (!res.ok) throw new Error('Export excel failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${id}_rating.xlsx`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export async function exportRatingExcel(id, suggestedName = null) {
  const res = await authedFetch(`${API_BASE_URL}/ratings/${id}/excel`);
  if (!res.ok) throw new Error('Excel export failed');
  const blob = await res.blob();
  let filename = suggestedName || `${id}_rating.xlsx`;
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  if (m) {
    try { filename = decodeURIComponent(m[1]); } catch { filename = m[1]; }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export async function upload(file, type = '') {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('type', type || '');
  const r = await authedFetch(`${API_BASE_URL}/upload`, { method: 'POST', body: fd });
  if (!r.ok) throw new Error('Upload failed');
  return r.json();
}

export async function summarize(id) {
  const r = await authedFetch(`${API_BASE_URL}/summarize/${id}`, { method: 'POST' });
  if (!r.ok) throw new Error('Summarize failed');
  return r.json();
}

export async function history() {
  const r = await authedFetch(`${API_BASE_URL}/history`);
  if (!r.ok) throw new Error('History failed');
  return r.json();
}

export async function transcript(id) {
  const r = await authedFetch(`${API_BASE_URL}/transcript/${id}`);
  if (!r.ok) throw new Error('Transcript failed');
  return r.json();
}

export async function downloadFile(id, suggestedName = null) {
  const res = await authedFetch(`${API_BASE_URL}/download/${id}`);
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const ct = (res.headers.get('Content-Type') || '').toLowerCase();
  let filename = suggestedName;
  if (!filename) {
    const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
    if (m) {
      try { filename = decodeURIComponent(m[1]); } catch { filename = m[1]; }
    } else {
      const ext = ct.includes('zip') ? '.zip' : '.txt';
      filename = `${id}${ext}`;
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export async function listTemplates(type = null) {
  const q = type ? `?template_type=${encodeURIComponent(type)}` : '';
  const r = await authedFetch(`${API_BASE_URL}/templates${q}`);
  if (!r.ok) throw new Error('List templates failed');
  return r.json();
}

export async function createTemplate(data) {
  const r = await authedFetch(`${API_BASE_URL}/templates`, {
    method:'POST',
    headers:{'Content-Type':'application/json'},
    body: JSON.stringify(data || {})
  });
  if (!r.ok) throw new Error('Create template failed');
  return r.json();
}

export async function updateTemplate(id, patch) {
  const r = await authedFetch(`${API_BASE_URL}/templates/${encodeURIComponent(id)}`, {
    method:'PUT',
    headers:{'Content-Type':'application/json'},
    body: JSON.stringify(patch || {})
  });
  if (!r.ok) throw new Error('Update template failed');
  return r.json();
}

export async function deleteTemplate(id) {
  const r = await authedFetch(`${API_BASE_URL}/templates/${encodeURIComponent(id)}`, {
    method:'DELETE'
  });
  if (!r.ok) throw new Error('Delete template failed');
  return r.json();
}

export async function setDefaultTemplate(id) {
  const r = await authedFetch(`${API_BASE_URL}/templates/${encodeURIComponent(id)}/set-default`, {
    method:'POST'
  });
  if (!r.ok) throw new Error('Set default template failed');
  return r.json();
}

export async function getDefaultTemplate(type /* 'online' | 'campaign' */) {
  const r = await authedFetch(`${API_BASE_URL}/templates/default?t=${encodeURIComponent(type)}`);
  if (!r.ok) throw new Error('Get default template failed');
  return r.json();
}

export async function getTemplateUsage(id) {
  const r = await fetch(`/api/templates/${id}/usage`, { credentials: 'include' });
  if (!r.ok) throw new Error('usage failed');
  return r.json();
}

export async function listModels() {
  const r = await authedFetch(`${API_BASE_URL}/models`);
  if (!r.ok) throw new Error('List models failed');
  return r.json();
}

export async function updateModel(modelName, patch) {
  const r = await authedFetch(`${API_BASE_URL}/models/${encodeURIComponent(modelName)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch || {})
  });
  if (!r.ok) throw new Error('Update model failed');
  return r.json();
}

export function getIdTokenPayload() {
  try {
    const raw = localStorage.getItem('kc_tokens');
    if (!raw) return null;
    const [, payload] = raw.split('.');
    return JSON.parse(atob(payload.replace(/-/g,'+').replace(/_/g,'/')));
  } catch { return null; }
}

export function hasRealmRole(role) {
  const p = getIdTokenPayload();
  const roles = p?.realm_access?.roles || [];
  return roles.includes(role);
}

export function hasClientRole(clientId, role) {
  const p = getIdTokenPayload();
  const roles = p?.resource_access?.[clientId]?.roles || [];
  return roles.includes(role);
}